import { isWabunkaError } from '@/models/error';
import { useValidationError } from '@/utils/typedError';
import { useSentry } from '@/wabunka-src/common/composables/general/useSentry/useSentry';
import { useRegisterWaitlistMutation } from '@/wabunka-src/features/Experience/composables/mutation/useRegisterWaitlistMutation';
import { createDefaultWaitlistRegistrationForm, validateWaitlistRegistrationForm, type WaitlistRegistrationForm } from '@/wabunka-src/features/Experience/models/waitlistRegistration';

/** WabunkaError 以外（ネットワーク・想定外）で表示する汎用メッセージ */
const GENERIC_SUBMIT_ERROR_MESSAGE = 'Something went wrong. Please try again, or contact us if the problem continues.';

/** $fetch のエラーからレスポンスボディ（WabunkaError 候補）を取り出す */
const getFetchErrorData = (e: unknown): unknown => {
  if (e !== null && typeof e === 'object' && 'data' in e) {
    return (e as { data: unknown }).data;
  }
  return undefined;
};

/**
 * 体験詳細ページのウェイトリスト登録モーダル用フォーム Composable
 *
 * バックエンドは既登録・不適格プラン・不存在プランのいずれも新規成功と同一の応答にするため（存在シグナルの非開示）、
 * このComposableも成功時の分岐を持たない。表示するのは入力バリデーションエラーとサーバエラーのみ。
 */
export const useWaitlistRegistrationForm = (experienceId: string) => {
  const { errors, setErrors, getErrorMessage, hasError } = useValidationError<WaitlistRegistrationForm>();
  const { captureWabunkaException } = useSentry();
  const isSending = ref(false);
  const serverErrorMessage = ref<string>('');
  const form = ref<WaitlistRegistrationForm>(createDefaultWaitlistRegistrationForm());

  const { registerMutate } = useRegisterWaitlistMutation();

  const reset = () => {
    form.value = createDefaultWaitlistRegistrationForm();
    setErrors([]);
    serverErrorMessage.value = '';
  };

  const onSubmit = async (onSuccess: () => void): Promise<void> => {
    // 再送信を試みた時点で前回のサーバエラーは古い。残すと入力エラーと二重に出る
    serverErrorMessage.value = '';
    setErrors(validateWaitlistRegistrationForm(form.value));
    if (hasError.value) return;
    // 連打防止
    if (isSending.value) return;

    isSending.value = true;
    try {
      await registerMutate(form.value, experienceId);
      reset();
      onSuccess();
    } catch (e) {
      const data = getFetchErrorData(e);
      if (isWabunkaError(data)) {
        // backend の 400 は入力形式エラーのみで、文言はプラン・メアドの存在に依存しない
        serverErrorMessage.value = data.message || GENERIC_SUBMIT_ERROR_MESSAGE;
      } else {
        serverErrorMessage.value = GENERIC_SUBMIT_ERROR_MESSAGE;
        captureWabunkaException(new Error(`Failed to register waitlist: ${e instanceof Error ? e.message : 'unknown error'}`));
      }
    } finally {
      isSending.value = false;
    }
  };

  return { form, isSending, errors, getErrorMessage, hasError, serverErrorMessage, onSubmit };
};
